import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Shield, 
  ArrowLeft,
  Check,
  X,
  Loader2,
  Clock,
  Mail,
  User,
  Calendar,
  Image,
  Eye,
  ChevronDown,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

export const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, getAuthHeaders, logout } = useAdminAuth();
  
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showProofDialog, setShowProofDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [plans, setPlans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pendente");

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/admin/login");
    }
  }, [loading, isAuthenticated, navigate]);

  // Load payments
  useEffect(() => {
    loadPayments();
  }, [isAuthenticated, getAuthHeaders, statusFilter]);

  // Load active plans
  useEffect(() => {
    const loadPlans = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await axios.get(`${API}/plans`, {
          headers: getAuthHeaders(),
        });
        const activePlans = (response.data.plans || []).filter((p) => p.active);
        setPlans(activePlans);
        if (activePlans.length > 0) setSelectedPlanId(activePlans[0].id);
      } catch (error) {
        console.error("Error loading plans:", error);
      }
    };
    loadPlans();
  }, [isAuthenticated, getAuthHeaders]);

  const loadPayments = async () => {
    if (!isAuthenticated) return;
    
    setLoadingPayments(true);
    try {
      const endpoint = statusFilter === "all" 
        ? `${API}/payments/all`
        : statusFilter === "pendente"
        ? `${API}/payments/pending`
        : `${API}/payments/all?status_filter=${statusFilter}`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setPayments(response.data.payments || []);
    } catch (error) {
      toast.error("Erro ao carregar pagamentos");
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleViewProof = async (payment) => {
    try {
      const response = await axios.get(`${API}/payments/${payment.id}`, {
        headers: getAuthHeaders(),
      });
      setSelectedPayment(response.data);
      setShowProofDialog(true);
    } catch (error) {
      toast.error("Erro ao carregar comprovante");
    }
  };

  const handleOpenAction = (payment, type) => {
    setSelectedPayment(payment);
    setActionType(type);
    if (plans.length > 0) setSelectedPlanId(plans[0].id);
    setShowActionDialog(true);
  };

  const handleReviewPayment = async () => {
    if (!selectedPayment || !actionType) return;
    
    setProcessing(true);
    try {
      const body = { action: actionType };
      if (actionType === "approve" && selectedPlanId) {
        body.plan_id = selectedPlanId;
      }
      
      const response = await axios.post(
        `${API}/payments/${selectedPayment.id}/review`,
        body,
        { headers: getAuthHeaders() }
      );
      
      toast.success(response.data.message);
      setShowActionDialog(false);
      setShowProofDialog(false);
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar pagamento");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pendente: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      aprovado: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      rejeitado: "bg-rose-500/20 text-rose-400 border-rose-500/30",
    };
    return badges[status] || badges.pendente;
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-payments-page">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm" className="gap-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-gray-900" />
              </div>
              <h1 className="font-heading text-xl font-bold text-gray-900">
                Pagamentos
              </h1>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            className="gap-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="animate-fade-in-up">
          {/* Filters */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold text-gray-900">
              {statusFilter === "pendente" ? "Pagamentos Pendentes" : "Todos os Pagamentos"}
            </h2>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white border-gray-300" data-testid="status-filter">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pendente">Pendentes</SelectItem>
                <SelectItem value="aprovado">Aprovados</SelectItem>
                <SelectItem value="rejeitado">Rejeitados</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingPayments ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
              <Clock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-400">
                {statusFilter === "pendente" 
                  ? "Nenhum pagamento pendente"
                  : "Nenhum pagamento encontrado"
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <>
                {payments.map((payment, index) => (
                  <div
                    key={payment.id}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`payment-item-${payment.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
                            <User className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{payment.user_name}</h3>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {payment.user_email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(payment.submitted_at)}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadge(payment.status)}`}>
                            {payment.status === "pendente" && "Pendente"}
                            {payment.status === "aprovado" && "Aprovado"}
                            {payment.status === "rejeitado" && "Rejeitado"}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewProof(payment)}
                          className="gap-2 text-gray-600 hover:text-gray-900"
                          data-testid={`view-proof-${payment.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          Ver Comprovante
                        </Button>
                        
                        {payment.status === "pendente" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleOpenAction(payment, "approve")}
                              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`approve-${payment.id}`}
                            >
                              <Check className="w-4 h-4" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleOpenAction(payment, "reject")}
                              className="gap-2"
                              data-testid={`reject-${payment.id}`}
                            >
                              <X className="w-4 h-4" />
                              Rejeitar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            </div>
          )}
        </div>
      </main>

      {/* View Proof Dialog */}
      <Dialog open={showProofDialog} onOpenChange={setShowProofDialog}>
        <DialogContent className="sm:max-w-2xl bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-heading">Comprovante de Pagamento</DialogTitle>
            <DialogDescription>
              {selectedPayment?.user_name} - {selectedPayment?.user_email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedPayment?.proof_image && (
              <div className="rounded-lg overflow-hidden bg-white p-2">
                <img 
                  src={selectedPayment.proof_image}
                  alt="Comprovante"
                  className="w-full h-auto max-h-[60vh] object-contain rounded"
                />
              </div>
            )}
          </div>
          
          {selectedPayment?.status === "pendente" && (
            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowProofDialog(false)}
                className="border border-gray-300"
              >
                Fechar
              </Button>
              <Button
                onClick={() => handleOpenAction(selectedPayment, "reject")}
                variant="destructive"
                className="gap-2"
              >
                <X className="w-4 h-4" />
                Rejeitar
              </Button>
              <Button
                onClick={() => handleOpenAction(selectedPayment, "approve")}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Check className="w-4 h-4" />
                Aprovar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-heading">
              {actionType === "approve" ? "Aprovar Pagamento" : "Rejeitar Pagamento"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve" 
                ? "Confirme a aprovação e defina o período de acesso."
                : "Confirme a rejeição do pagamento. O usuário poderá enviar novo comprovante."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500">Usuário</p>
              <p className="text-gray-900 font-medium">{selectedPayment?.user_name}</p>
              <p className="text-sm text-gray-500">{selectedPayment?.user_email}</p>
            </div>
            
            {actionType === "approve" && (
              <div className="space-y-2">
                <Label className="text-gray-900/80">Plano de acesso</Label>
                {plans.length > 0 ? (
                  <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                    <SelectTrigger className="bg-white border-gray-300" data-testid="plan-select">
                      <SelectValue placeholder="Selecione um plano" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name} ({plan.duration_days} dias)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-amber-400">
                    Nenhum plano ativo. Configure os planos em Configurações.
                  </p>
                )}
                {selectedPlanId && (
                  <p className="text-xs text-gray-500">
                    O usuário receberá{" "}
                    {plans.find((p) => p.id === selectedPlanId)?.duration_days || 0} dias
                    de acesso.
                  </p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowActionDialog(false)}
              className="border border-gray-300"
              disabled={processing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReviewPayment}
              disabled={processing}
              className={actionType === "approve" 
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-rose-600 hover:bg-rose-700"
              }
              data-testid="confirm-action-button"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : actionType === "approve" ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirmar Aprovação
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Confirmar Rejeição
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentsPage;
